/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-undef */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  IconSearch,
  IconPencil,
  IconShoppingCart,
  IconAlertCircle,
  IconUserPlus,
  IconUsers,
  IconClock,
  IconMail,
  IconTrash,
  IconChevronDown,
  IconRefreshCw,
} from '../credit-plans/SVGIcons';
import LicenseBadge, { LicenseType } from './LicenseBadge';
import { buildLicenseInventory } from 'utils/licenseInventory';
import PendingInviteAPI from 'api/PendingInviteAPI';
import { useSnackbar } from 'notistack';
import InviteModal from '../../components/Profile/InviteModal';
import InviteFailModal from 'components/Profile/InviteFailModal';
import RemoveUserModal from 'components/Profile/RemoveUserModal';
import CreateTeamModal from 'components/Profile/CreateTeamModal';
import DeleteTeamModal from 'components/TeamManagement/DeleteTeamModal';
import { Modal } from '@mui/material';
import { inviteUserNotification } from 'utils/NotificationsFuncs';
import EmailAPI from 'api/EmailAPI';
import UsersAPI from 'api/UsersAPI';
import GroupAPI from 'api/GroupAPI';
import TeamsAPI from 'api/TeamsAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import LicensesAPI from 'api/LicensesAPI';
import { getPrimaryTeamAdmin, isTeamAdmin } from 'utils/teamAdmin';

interface TeamManagementProps {
  cur_user: any;
  handleTabChange: any;
  cusId: string;
}

type ViewMode = 'members' | 'pending';
type LoginSort = 'none' | 'asc' | 'desc';
const LICENSE_OPTIONS: LicenseType[] = [
  'standard',
  'pro',
  'business',
  'enterprise',
  'none',
];

const ITEMS_PER_PAGE = 10;
const INVITES_PER_PAGE = 10;

const resolveMemberId = (member: any) =>
  member?.id ?? member?.user_id ?? member?.userId ?? null;

type MemberRole = 'Owner' | 'Admin' | 'Contributor' | 'Restricted';

const normalizeRole = (rawRole: any): MemberRole => {
  const normalized = String(rawRole ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'owner') {
    return 'Owner';
  }
  if (normalized === 'admin') {
    return 'Admin';
  }
  if (normalized === 'domain-restricted' || normalized === 'restricted') {
    return 'Restricted';
  }
  return 'Contributor';
};

interface PendingInviteRecord {
  id?: string;
  email: string;
  preassigned_license_type: LicenseType;
}

const resolveLicenseType = (rawLicense: string): LicenseType => {
  const normalized = rawLicense.toLowerCase();
  if (normalized === 'starter') {
    return 'standard';
  }
  if (
    normalized === 'standard' ||
    normalized === 'pro' ||
    normalized === 'business' ||
    normalized === 'enterprise'
  ) {
    return normalized;
  }
  return 'none';
};

const normalizePendingInvite = (invite: any): PendingInviteRecord => ({
  id: invite?.id ? String(invite.id) : undefined,
  email: String(invite?.email ?? invite ?? '').trim(),
  preassigned_license_type: resolveLicenseType(
    String(invite?.preassigned_license_type ?? 'none'),
  ),
});

const TeamManagement: React.FC<TeamManagementProps> = ({
  cur_user,
  handleTabChange,
  cusId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('members');
  const [searchValue, setSearchValue] = useState('');
  const [members, setMembers] = useState<any>([]);
  const [pending, setPending] = useState<PendingInviteRecord[]>([]);
  const [inviteMode, setInviteMode] = useState<boolean>(false);
  const [inviteFailMode, setInviteFailMode] = useState<boolean>(false);
  const [removeMode, setRemoveMode] = useState<boolean>(false);
  const [newMode, setNewMode] = useState<boolean>(false);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [createTeamInviteEmails, setCreateTeamInviteEmails] = useState<
    string[]
  >([]);
  const [currentCreateTeamInviteEmail, setCurrentCreateTeamInviteEmail] =
    useState<string>('');
  const [selectedUser, setSelectedUser] = useState<any>({
    isLoading: true,
    data: {},
  });
  const [loginCountsById, setLoginCountsById] = useState<
    Record<string, number | null>
  >({});
  const [loginCountLoadingById, setLoginCountLoadingById] = useState<
    Record<string, boolean>
  >({});
  const [licenseByMemberId, setLicenseByMemberId] = useState<
    Record<string, LicenseType>
  >({});
  const [stripeCustomer, setStripeCustomer] = useState<any>(null);
  const [roleUpdateByMemberId, setRoleUpdateByMemberId] = useState<
    Record<string, boolean>
  >({});
  const { enqueueSnackbar } = useSnackbar();
  // Table filtering control state
  const [licenseFilter, setLicenseFilter] = useState<LicenseType | 'all'>(
    'all',
  );
  const [loginSort, setLoginSort] = useState<LoginSort>('none');
  const [showLicenseDropdown, setShowLicenseDropdown] = useState(false);

  const membersWithLicenses = useMemo(
    () =>
      (Array.isArray(members) ? members : []).map((member: any) => {
        const memberId = resolveMemberId(member);
        const assignedLicense = memberId ? licenseByMemberId[memberId] : null;
        return {
          ...member,
          license: resolveLicenseType(
            assignedLicense ?? member?.license ?? 'none',
          ),
        };
      }),
    [licenseByMemberId, members],
  );

  const licenseInventory = useMemo<
    Record<LicenseType, { total: number; used: number }>
  >(() => {
    const inventory: Record<LicenseType, { total: number; used: number }> = {
      standard: { total: 0, used: 0 },
      pro: { total: 0, used: 0 },
      business: { total: 0, used: 0 },
      enterprise: { total: 0, used: 0 },
      none: { total: Infinity, used: 0 },
    };

    const matchingCustomer = stripeCustomer;

    const subscriptions = Array.isArray(matchingCustomer?.subscriptions?.data)
      ? matchingCustomer.subscriptions.data
      : [];

    const licenseTotals = buildLicenseInventory(subscriptions);
    inventory.standard.total = licenseTotals.standard;
    inventory.pro.total = licenseTotals.pro;
    inventory.business.total = licenseTotals.business;
    inventory.enterprise.total = licenseTotals.enterprise;

    membersWithLicenses.forEach((member: any) => {
      const licenseType = resolveLicenseType(member?.license ?? 'none');
      inventory[licenseType].used += 1;
    });

    return inventory;
  }, [membersWithLicenses, stripeCustomer]);

  useEffect(() => {
    const teamAdminEmail = getPrimaryTeamAdmin(cur_user?.selected_team) ?? null;
    const userEmail = teamAdminEmail || cur_user?.basics?.email;

    if (!userEmail) {
      setStripeCustomer(null);
      return;
    }

    let isCurrent = true;

    const fetchStripeCustomer = async () => {
      const stripeRes = await PaymentsAPI.getCustomerByEmail(userEmail);
      if (!isCurrent) {
        return;
      }

      if (!stripeRes?.success) {
        setStripeCustomer(null);
        enqueueSnackbar('Unable to fetch latest license totals', {
          variant: 'warning',
        });
        return;
      }

      const customers = Array.isArray(stripeRes?.result?.data)
        ? stripeRes.result.data
        : [];

      const selectedTeamName = cur_user?.selected_team?.name;
      const matchingCustomer =
        customers.find((customer: any) => {
          if (cusId) {
            return customer?.id === cusId;
          }
          if (selectedTeamName) {
            return customer?.name === selectedTeamName;
          }
          return false;
        }) ?? null;

      setStripeCustomer(matchingCustomer);
    };

    fetchStripeCustomer().catch(() => {
      if (!isCurrent) {
        return;
      }

      setStripeCustomer(null);
      enqueueSnackbar('Unable to fetch latest license totals', {
        variant: 'error',
      });
    });

    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [cur_user?.basics?.email, cusId, enqueueSnackbar]);

  const watchlistRoleId = process.env.REACT_APP_WATCHLIST_ID!;
  const cyberProtectionRoleId = process.env.REACT_APP_CYBER_PROTECTION_ID!;
  const biteListRoleId = process.env.REACT_APP_BITE_LIST_ID!;
  const tradeProtRoleId = process.env.REACT_APP_TRADE_PROTECTION_ID!;
  const biteScoreRoleId = process.env.REACT_APP_BITE_SCORE_ID!;
  const biteVizRoleId = process.env.REACT_APP_BITE_VIZ_ID!;

  const sanitizeEmail = (email: string): string =>
    email.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').trim();

  const normalizeEmails = (value: string): string[] =>
    value
      .split(/[\s,;]+/)
      .map((email) => sanitizeEmail(email))
      .filter(Boolean);

  const validateEmail = (email: string): boolean => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return Boolean(email) && isValidEmail.test(email);
  };

  const getCreateTeamInviteEmails = (): string[] => {
    const allEmails = [
      ...createTeamInviteEmails,
      ...normalizeEmails(currentCreateTeamInviteEmail),
    ];

    return allEmails.filter(
      (email, index) =>
        allEmails.findIndex(
          (currentEmail) => currentEmail.toLowerCase() === email.toLowerCase(),
        ) === index,
    );
  };

  const handleAddCreateTeamInviteEmail = () => {
    const nextEmails = normalizeEmails(currentCreateTeamInviteEmail);

    if (nextEmails.length === 0) {
      return;
    }

    setCreateTeamInviteEmails((prevEmails) => {
      const existingEmails = new Set(
        prevEmails.map((email) => email.toLowerCase()),
      );
      const uniqueEmails = nextEmails.filter(
        (email) => !existingEmails.has(email.toLowerCase()),
      );

      return [...prevEmails, ...uniqueEmails];
    });
    setCurrentCreateTeamInviteEmail('');
  };

  const handleRemoveCreateTeamInviteEmail = (index: number) => {
    setCreateTeamInviteEmails((prevEmails) =>
      prevEmails.filter((_, emailIndex) => emailIndex !== index),
    );
  };

  const handleCreateTeamInviteKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddCreateTeamInviteEmail();
      return;
    }

    if (
      e.key === 'Backspace' &&
      currentCreateTeamInviteEmail === '' &&
      createTeamInviteEmails.length > 0
    ) {
      setCreateTeamInviteEmails((prevEmails) => prevEmails.slice(0, -1));
    }
  };

  const sendCreateTeamInvite = async (
    emailValue: string,
    teamId: string,
    teamName: string,
  ) => {
    const email = sanitizeEmail(emailValue);

    if (!validateEmail(email)) {
      enqueueSnackbar(`Skipped invalid email: ${emailValue}`, {
        variant: 'warning',
      });
      return false;
    }

    try {
      const res = await EmailAPI.inviteUserToTeam({
        requestorEmail: cur_user?.basics?.email,
        teamID: teamId,
        teamName: teamName,
        formValues: { email: email },
      });

      if (!res?.success) {
        const message: string =
          res && res.result && typeof res.result === 'string'
            ? res.result
            : `Unable to send team invite to ${email}`;
        enqueueSnackbar(message, { variant: 'error' });
        return false;
      }

      const pendingResp = await PendingInviteAPI.addPendingInvite(
        teamId,
        email.toLowerCase(),
      );
      if (!pendingResp?.success) {
        enqueueSnackbar(
          'Invite email sent, but pending invite record was not saved.',
          {
            variant: 'warning',
          },
        );
      }

      const userResp = await UsersAPI.searchUsers(email);
      if (userResp?.success && userResp.result.length > 0) {
        const userId = userResp.result[0].id;
        await inviteUserNotification(
          cur_user,
          {
            id: teamId,
            name: teamName,
          },
          userId,
        );
      } else if (!userResp?.success) {
        enqueueSnackbar('Unable to send invite notification to user', {
          variant: 'error',
        });
      }

      return true;
    } catch {
      enqueueSnackbar(`Unable to send team invite to ${email}`, {
        variant: 'error',
      });
      return false;
    }
  };

  const handleCreateNew = async (value: any) => {
    if (value === '') {
      return;
    }
    // removes any extra whitespaces in team name from input field
    const formattedName = value.trim();
    const new_team: any = {
      name: formattedName,
      team_admin_id: cur_user?.basics?.email,
      team_id: '',
      keycloakUserId: cur_user?.basics?.id,
    };
    const resp = await TeamsAPI.createTeam({ newTeam: new_team });
    if (resp?.success && cur_user?.basics?.id) {
      const teamId = resp.result.id;
      new_team.team_id = teamId;
      const addUser = await GroupAPI.assignGroupToUser(
        teamId,
        cur_user?.basics?.id,
      );
      if (addUser?.success) {
        const createStripeCustomer = await PaymentsAPI.createCustomer(new_team);
        if (createStripeCustomer?.success) {
          const addWatchlistRole = await GroupAPI.addRoleToGroup(
            teamId,
            watchlistRoleId,
            'watchlist-data',
          );
          if (addWatchlistRole?.success) {
            const addCyberRole = await GroupAPI.addRoleToGroup(
              teamId,
              cyberProtectionRoleId,
              'cyber-protection',
            );
            if (addCyberRole?.success) {
              const addBiteListRole = await GroupAPI.addRoleToGroup(
                teamId,
                biteListRoleId,
                'bite-list',
              );
              if (addBiteListRole?.success) {
                const addTradeProtRole = await GroupAPI.addRoleToGroup(
                  teamId,
                  tradeProtRoleId,
                  'trade-protection',
                );
                if (addTradeProtRole?.success) {
                  const addBiteScoreRole = await GroupAPI.addRoleToGroup(
                    teamId,
                    biteScoreRoleId,
                    'bite-score',
                  );
                  if (addBiteScoreRole?.success) {
                    const addBiteVizRole = await GroupAPI.addRoleToGroup(
                      teamId,
                      biteVizRoleId,
                      'customizable',
                    );
                    if (addBiteVizRole?.success) {
                      const inviteEmails = getCreateTeamInviteEmails();
                      let successfulInvites = 0;

                      for (const email of inviteEmails) {
                        const inviteSuccess = await sendCreateTeamInvite(
                          email,
                          teamId,
                          formattedName,
                        );

                        if (inviteSuccess) {
                          successfulInvites += 1;
                        }
                      }

                      const error_msg = 'New team created';
                      enqueueSnackbar(error_msg, { variant: 'success' });
                      if (successfulInvites > 0) {
                        enqueueSnackbar(
                          successfulInvites === 1
                            ? 'Team invite sent via Email'
                            : `${successfulInvites} team invites sent via Email`,
                          { variant: 'success' },
                        );
                      }
                      history.go(0);
                    }
                  }
                }
              }
            }
          }
        }
      }
    } else {
      const error_msg =
        'Error creating new team. Team name may already be in use';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const handleRemove = (user: string) => {
    if (!canManageMembers) {
      enqueueSnackbar('Only admins or owners can remove users', {
        variant: 'warning',
      });
      return;
    }

    setRemoveMode(true);
    setSelectedUser({ isLoading: false, data: user });
  };

  const handleResend = async (email: string) => {
    await EmailAPI.inviteUserToTeam({
      requestorEmail: cur_user?.basics?.email,
      teamID: cur_user.selected_team?.id,
      teamName: cur_user.selected_team?.name,
      formValues: { email: email },
    })
      .then(async (res) => {
        if (res?.success) {
          const res3 = await UsersAPI.searchUsers(email);
          if (res3?.success) {
            if (res3.result.length > 0) {
              const userId = res3.result[0].id;
              await inviteUserNotification(
                cur_user,
                cur_user.selected_team,
                userId,
              );
              enqueueSnackbar('User received notifaction to join your team', {
                variant: 'success',
              });
            }
          } else {
            enqueueSnackbar('Unable to send invite notification to user', {
              variant: 'error',
            });
          }
        } else {
          const message: string =
            res && res.result && typeof res.result === 'string'
              ? res.result
              : 'Unable to send team invite via Email';
          enqueueSnackbar(message, { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to send team invite via Email', {
          variant: 'error',
        });
      });
  };

  const handleRevoke = async (email: string) => {
    const res = await PendingInviteAPI.removePendingInvite(
      cur_user.selected_team.id,
      email,
    );
    if (res?.success) {
      enqueueSnackbar('User invite has been revoked', { variant: 'success' });
      history.go(0);
    } else {
      enqueueSnackbar('Unable to revoke invite at this time', {
        variant: 'error',
      });
    }
  };

  const removeUserFromGroup = async (user_id: string) => {
    if (!canManageMembers) {
      enqueueSnackbar('Only admins or owners can remove users', {
        variant: 'warning',
      });
      return;
    }

    const resp = await GroupAPI.removeUserFromGroup(
      cur_user.selected_team.id,
      user_id,
    );
    if (resp?.success) {
      enqueueSnackbar('User removed successfully', { variant: 'info' });
      history.go(0);
    } else {
      const err_text = `Error removing user: ${resp?.result}`;
      enqueueSnackbar(err_text, { variant: 'error' });
    }
  };

  useEffect(() => {
    setMembers(
      Array.isArray(cur_user.selected_team.members)
        ? cur_user.selected_team.members
        : [],
    );
  }, []);

  useEffect(() => {
    const teamId = cur_user?.selected_team?.id;
    if (!teamId) {
      setLicenseByMemberId({});
      return;
    }

    let isCurrent = true;

    const fetchLicenses = async () => {
      try {
        const res = await LicensesAPI.getLicensesForTeam(teamId);

        if (!isCurrent) {
          return;
        }

        if (!res?.success) {
          enqueueSnackbar('Unable to fetch team licenses', {
            variant: 'warning',
          });
          setLicenseByMemberId({});
          return;
        }

        const assignments = Array.isArray(res?.result) ? res.result : [];
        const assignmentMap: Record<string, LicenseType> = {};
        const roleByMemberId: Record<string, MemberRole> = {};
        const scimManagedByMemberId: Record<string, boolean> = {};

        assignments.forEach((assignment: any) => {
          const memberId = assignment?.memberId;
          if (!memberId) {
            return;
          }

          roleByMemberId[String(memberId)] = normalizeRole(
            assignment?.role ?? assignment?.memberRole ?? assignment?.userRole,
          );

          assignmentMap[String(memberId)] = resolveLicenseType(
            assignment?.currentLicense ?? 'none',
          );

          scimManagedByMemberId[String(memberId)] = Boolean(
            assignment?.scimManaged,
          );
        });

        setLicenseByMemberId(assignmentMap);
        setMembers((prevMembers: any[]) =>
          prevMembers.map((member: any) => {
            const memberId = resolveMemberId(member);
            const fetchedRole = memberId
              ? roleByMemberId[String(memberId)]
              : null;
            const fallbackRole = isTeamAdmin(
              cur_user?.selected_team,
              member?.email,
            )
              ? 'Admin'
              : 'Contributor';

            return {
              ...member,
              role: fetchedRole ?? normalizeRole(member?.role ?? fallbackRole),
              scimManaged: memberId
                ? Boolean(scimManagedByMemberId[String(memberId)])
                : false,
            };
          }),
        );
      } catch (error: any) {
        if (!isCurrent) {
          return;
        }

        // --- THE FIX ---
        // If the error is a 401, the Axios Interceptor is already handling the logout.
        // We do NOT want to show a snackbar or wipe the state, we just want to silently wait for the redirect.
        if (error?.response?.status === 401) {
          return;
        }

        // For any other legitimate network error, show the warning
        enqueueSnackbar('Unable to fetch team licenses', {
          variant: 'error',
        });
        setLicenseByMemberId({});
      }
    };

    fetchLicenses();
    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [cur_user?.selected_team?.id, enqueueSnackbar]);

  useEffect(() => {
    // remove empty placeholder pending value
    PendingInviteAPI.fetchTeamPendingInvites(cur_user?.selected_team?.id, true)
      .then((res) => {
        if (res?.success) {
          const pendingInvites = Array.isArray(res?.result?.data)
            ? res.result.data.map((u: any) => normalizePendingInvite(u))
            : [];
          setPending(pendingInvites);
        }
      })
      .catch(() => {
        enqueueSnackbar('Unable to fetch pending invites', {
          variant: 'error',
        });
      });
  }, [cur_user]);

  const getInviteEntries = (
    formVals: any,
  ): Array<{ email: string; license: LicenseType }> => {
    if (Array.isArray(formVals?.invites)) {
      return formVals.invites
        .map((invite: any) => ({
          email: String(invite?.email ?? '').trim(),
          license: resolveLicenseType(String(invite?.license ?? 'none')),
        }))
        .filter((invite: { email: string }) => Boolean(invite.email));
    }

    if (Array.isArray(formVals?.emails)) {
      return formVals.emails
        .map((email: string) => ({
          email: String(email).trim(),
          license: 'none' as LicenseType,
        }))
        .filter((invite: { email: string }) => Boolean(invite.email));
    }

    if (typeof formVals?.email === 'string' && formVals.email.trim() !== '') {
      return [{ email: formVals.email.trim(), license: 'none' }];
    }

    return [];
  };

  const sendInvite = async (formVals: any) => {
    const inviteEntries = getInviteEntries(formVals);
    const emails = inviteEntries.map((entry) => entry.email);
    const hasInvalidEmail = emails.some((email) => !validateEmail(email));

    if (emails.length === 0 || hasInvalidEmail) {
      enqueueSnackbar('Email is invalid in valid', { variant: 'error' });
      return false;
    }

    const pendingReservedCounts = pending.reduce(
      (acc, invite) => {
        const license = resolveLicenseType(
          invite?.preassigned_license_type ?? 'none',
        );
        if (license !== 'none') {
          acc[license] += 1;
        }
        return acc;
      },
      { standard: 0, pro: 0, business: 0, enterprise: 0, none: 0 },
    );

    const newInviteLicenseCounts = inviteEntries.reduce(
      (acc, invite) => {
        if (invite.license !== 'none') {
          acc[invite.license] += 1;
        }
        return acc;
      },
      { standard: 0, pro: 0, business: 0, enterprise: 0, none: 0 },
    );

    for (const license of [
      'standard',
      'pro',
      'business',
      'enterprise',
    ] as LicenseType[]) {
      const inventory = licenseInventory[license] ?? { total: 0, used: 0 };
      const available = Math.max(
        0,
        Number(inventory.total ?? 0) -
          Number(inventory.used ?? 0) -
          Number(pendingReservedCounts[license] ?? 0),
      );

      if (Number(newInviteLicenseCounts[license] ?? 0) > available) {
        enqueueSnackbar(
          `Unable to assign ${license} licenses. Only ${available} available.`,
          { variant: 'error' },
        );
        return false;
      }
    }

    if (!cur_user?.selected_team?.attributes?.pending) {
      enqueueSnackbar('Unable to send team invite via Email', {
        variant: 'error',
      });
      return false;
    }

    let successfulInvites = 0;

    for (const inviteEntry of inviteEntries) {
      const email = inviteEntry.email;
      try {
        const res = await EmailAPI.inviteUserToTeam({
          requestorEmail: cur_user?.basics?.email,
          teamID: cur_user.selected_team?.id,
          teamName: cur_user.selected_team?.name,
          formValues: { email: email },
        });

        if (!res?.success) {
          const message: string =
            res && res.result && typeof res.result === 'string'
              ? res.result
              : `Unable to send team invite to ${email}`;
          enqueueSnackbar(message, { variant: 'error' });
          continue;
        }

        const res2 = await PendingInviteAPI.addPendingInvite(
          cur_user.selected_team?.id,
          email.toLowerCase(),
          inviteEntry.license,
        );
        if (res2?.success) {
          successfulInvites += 1;
        }

        const res3 = await UsersAPI.searchUsers(email);
        if (res3?.success) {
          if (res3.result.length > 0) {
            const userId = res3.result[0].id;
            await inviteUserNotification(
              cur_user,
              cur_user.selected_team,
              userId,
            );
          }
        } else {
          enqueueSnackbar('Unable to send invite notification to user', {
            variant: 'error',
          });
        }
      } catch {
        enqueueSnackbar(`Unable to send team invite to ${email}`, {
          variant: 'error',
        });
      }
    }

    if (successfulInvites > 0) {
      enqueueSnackbar(
        successfulInvites === 1
          ? 'Team invite sent via Email'
          : `${successfulInvites} team invites sent via Email`,
        { variant: 'success' },
      );
      history.go(0);
      return true;
    }

    enqueueSnackbar('Unable to send team invite via Email', {
      variant: 'error',
    });
    return false;
  };

  // STEP 1a: Create a stable string of member IDs to prevent unnecessary re-runs
  const memberIdsString = useMemo(() => {
    const ids = membersWithLicenses
      .map((member: any) => resolveMemberId(member))
      .filter((id: string | null): id is string => Boolean(id));

    // Sort and join to create a stable string (e.g., "1,2,3")
    return Array.from(new Set(ids)).sort().join(',');
  }, [membersWithLicenses]);

  // Keep the ref at the top of your component
  const processedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (viewMode !== 'members' || !memberIdsString) {
      return;
    }

    const userIds = memberIdsString.split(',');

    // FIX: Check for key existence ('id in loginCountsById')
    // instead of truthiness. This treats null/0 as "already handled."
    const idsToFetch = userIds.filter(
      (id) => !processedIdsRef.current.has(id) && !(id in loginCountsById),
    );

    if (idsToFetch.length === 0) {
      return;
    }

    // Synchronously lock these IDs
    idsToFetch.forEach((id) => processedIdsRef.current.add(id));

    const fetchCounts = async () => {
      setLoginCountLoadingById((prev) => ({
        ...prev,
        ...Object.fromEntries(idsToFetch.map((id) => [id, true])),
      }));

      try {
        const results = await Promise.all(
          idsToFetch.map(async (userId: string) => ({
            userId: userId,
            response: await UsersAPI.getLoginEventCount(userId, 30),
          })),
        );

        const nextCounts: Record<string, number | null> = {};
        let hasErrors = false;

        results.forEach(({ userId, response }) => {
          if (
            response?.success &&
            typeof response?.result?.count === 'number'
          ) {
            nextCounts[userId] = response.result.count;
          } else {
            // Terminal failure marker: record null so 'id in loginCountsById' returns true
            nextCounts[userId] = null;
            hasErrors = true;
          }
        });

        setLoginCountsById((prev) => ({ ...prev, ...nextCounts }));

        if (hasErrors) {
          enqueueSnackbar('Some login counts could not be retrieved.', {
            variant: 'warning',
          });
        }
      } catch (error) {
        // On a total catastrophic failure (network down, etc.), we record nulls
        // for this batch so we don't immediately loop.
        const errorBatch: Record<string, null> = {};
        idsToFetch.forEach((id) => {
          errorBatch[id] = null;
        });
        setLoginCountsById((prev) => ({ ...prev, ...errorBatch }));

        enqueueSnackbar('Unable to fetch login counts', { variant: 'error' });
      } finally {
        setLoginCountLoadingById((prev) => ({
          ...prev,
          ...Object.fromEntries(idsToFetch.map((id) => [id, false])),
        }));
      }
    };

    fetchCounts();
  }, [viewMode, memberIdsString, loginCountsById]);

  // STEP 2: Combine base data, licenses, and counts into one source of truth
  const membersWithAllData = useMemo(() => {
    return membersWithLicenses.map((member: any) => {
      const memberId = resolveMemberId(member);
      const isLoading = memberId
        ? Boolean(loginCountLoadingById[memberId])
        : false;
      const loginCount =
        memberId && memberId in loginCountsById
          ? loginCountsById[memberId]
          : null;

      return {
        ...member,
        isLoginCountLoading: isLoading,
        loginsRaw: loginCount ?? 0,
        loginsDisplay: loginCount ?? '—',
      };
    });
  }, [membersWithLicenses, loginCountsById, loginCountLoadingById]);

  // STEP 3: Filter and sort on the combined data
  const normalizedSearch = searchValue.trim().toLowerCase();

  const filteredMembers = useMemo(() => {
    let result: any[] = [...membersWithAllData];

    if (normalizedSearch) {
      result = result.filter((member: any) => {
        const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`
          .trim()
          .toLowerCase();
        const email = member.email?.toLowerCase() ?? '';
        return (
          name.includes(normalizedSearch) || email.includes(normalizedSearch)
        );
      });
    }

    if (licenseFilter !== 'all') {
      result = result.filter((m) => m.license === licenseFilter);
    }

    if (loginSort === 'asc') {
      result.sort((a, b) => a.loginsRaw - b.loginsRaw);
    } else if (loginSort === 'desc') {
      result.sort((a, b) => b.loginsRaw - a.loginsRaw);
    }

    return result;
  }, [membersWithAllData, normalizedSearch, licenseFilter, loginSort]);

  const cycleLoginSort = () => {
    setLoginSort((prev) =>
      prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none',
    );
    setCurrentPage(1);
  };

  const handleLicenseFilter = (val: LicenseType | 'all') => {
    setLicenseFilter(val);
    setShowLicenseDropdown(false);
    setCurrentPage(1);
  };

  const filteredPending = useMemo(() => {
    if (!normalizedSearch) {
      return pending;
    }
    return pending.filter((invite: any) => {
      const email = invite?.email?.toLowerCase?.() ?? `${invite}`.toLowerCase();
      return email.includes(normalizedSearch);
    });
  }, [pending, normalizedSearch]);

  const membersTotalPages = Math.max(
    1,
    Math.ceil(filteredMembers.length / ITEMS_PER_PAGE),
  );
  const pendingTotalPages = Math.max(
    1,
    Math.ceil(filteredPending.length / INVITES_PER_PAGE),
  );

  const totalPages =
    viewMode === 'members' ? membersTotalPages : pendingTotalPages;
  const itemsPerPage =
    viewMode === 'members' ? ITEMS_PER_PAGE : INVITES_PER_PAGE;

  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, normalizedSearch]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  // STEP 4: Paginate the fully sorted and filtered data
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMembers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredMembers, currentPage]);

  const paginatedPending = useMemo(() => {
    const startIndex = (currentPage - 1) * INVITES_PER_PAGE;
    return filteredPending.slice(startIndex, startIndex + INVITES_PER_PAGE);
  }, [filteredPending, currentPage]);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    setCurrentPage(1);
    setSearchValue('');
    setLicenseFilter('all');
    setLoginSort('none');
  };

  const totalItems =
    viewMode === 'members' ? filteredMembers.length : filteredPending.length;

  const currentUserRole = useMemo<MemberRole>(() => {
    const currentMember = members.find((member: any) => {
      const memberId = resolveMemberId(member);
      return (
        String(memberId ?? '') === String(cur_user?.basics?.id ?? '') ||
        String(member?.email ?? '').toLowerCase() ===
          String(cur_user?.basics?.email ?? '').toLowerCase()
      );
    });

    return normalizeRole(currentMember?.role ?? cur_user?.role);
  }, [members, cur_user?.basics?.email, cur_user?.basics?.id, cur_user?.role]);

  const canManageMembers =
    currentUserRole === 'Admin' || currentUserRole === 'Owner';

  const updateMemberRole = async (memberId: string, role: MemberRole) => {
    if (!canManageMembers) {
      enqueueSnackbar('Only admins or owners can change member roles', {
        variant: 'warning',
      });
      return;
    }

    // Belt and braces behind the disabled control: a managed member's role is
    // re-applied from their identity provider at every sign-in, so a change here
    // would be silently undone.
    const target = members.find(
      (member: any) => String(resolveMemberId(member) ?? member.id) === String(memberId),
    );
    if (target?.scimManaged) {
      enqueueSnackbar(
        "This member's role is managed by their organization's identity provider",
        { variant: 'info' },
      );
      return;
    }

    // A Restricted member's access comes from their email domain, and only the
    // domain sync grants it. Changing it here would remove that access with no
    // way back, so the server refuses it too.
    if (normalizeRole(target?.role) === 'Restricted') {
      enqueueSnackbar(
        "This member's role comes from their email domain and cannot be changed",
        { variant: 'info' },
      );
      return;
    }

    const teamId = cur_user?.selected_team?.id;
    const adminId = cur_user?.basics?.id;

    if (!teamId || !adminId || !memberId) {
      enqueueSnackbar('Unable to change user role at this time', {
        variant: 'error',
      });
      return;
    }

    const normalizedRole = normalizeRole(role);

    if (normalizedRole === 'Owner') {
      enqueueSnackbar('Owner role cannot be changed', {
        variant: 'warning',
      });
      return;
    }
    const previousRole = normalizeRole(
      members.find(
        (member: any) => String(resolveMemberId(member)) === String(memberId),
      )?.role,
    );

    setRoleUpdateByMemberId((prev) => ({
      ...prev,
      [String(memberId)]: true,
    }));

    setMembers((prevMembers: any[]) =>
      prevMembers.map((member: any) => {
        if (String(resolveMemberId(member)) !== String(memberId)) {
          return member;
        }

        return {
          ...member,
          role: normalizedRole,
        };
      }),
    );

    try {
      // Map UI role back to database string
      const apiRolePayload =
        normalizedRole === 'Restricted'
          ? 'domain-restricted'
          : normalizedRole.toLowerCase();
      const res = await LicensesAPI.changeRole(teamId, {
        memberId: String(memberId),
        adminId: String(adminId),
        role: apiRolePayload,
      });

      if (!res?.success) {
        setMembers((prevMembers: any[]) =>
          prevMembers.map((member: any) => {
            if (String(resolveMemberId(member)) !== String(memberId)) {
              return member;
            }

            return {
              ...member,
              role: previousRole,
            };
          }),
        );

        enqueueSnackbar('Unable to update member role', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('User role was successfully changed', {
          variant: 'success',
        });
      }
    } catch {
      setMembers((prevMembers: any[]) =>
        prevMembers.map((member: any) => {
          if (String(resolveMemberId(member)) !== String(memberId)) {
            return member;
          }

          return {
            ...member,
            role: previousRole,
          };
        }),
      );

      enqueueSnackbar('Unable to update member role', {
        variant: 'error',
      });
    } finally {
      setRoleUpdateByMemberId((prev) => ({
        ...prev,
        [String(memberId)]: false,
      }));
    }
  };

  const handleExportCSV = () => {
    if (filteredMembers.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    // 1. Define the CSV headers
    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'License',
      'Logins',
      'Role',
    ];

    // 2. Map the data into an array of CSV rows
    const csvData = filteredMembers.map((m: any) => {
      const row = [
        m.firstName || '',
        m.lastName || '',
        m.email || '',
        m.license || 'none',
        m.isLoginCountLoading ? 'Calculating...' : m.loginsRaw ?? 0,
        normalizeRole(m.role) || '',
      ];

      // Escape existing quotes and wrap each field in quotes to safely handle commas in the data
      return row
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(',');
    });

    // 3. Combine headers and rows
    const csvString = [headers.join(','), ...csvData].join('\n');

    // 4. Create a Blob and trigger the browser download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Fallback to "team_members.csv" if the team name isn't loaded
    const teamName = cur_user?.selected_team?.name
      ? cur_user.selected_team.name.replace(/\s+/g, '_')
      : 'team';

    link.href = url;
    link.setAttribute('download', `${teamName}_members.csv`);

    // Append to body, click, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className='tm-root'>
      {/* Section Header */}
      <div className='tm-section-header'>
        <div className='tm-section-title'>Team Overview</div>
        <div className='tm-section-subtitle'>
          View your team members and their current license assignments
        </div>
      </div>
      {/* Header */}
      <div className='tm-header'>
        <div className='tm-search-wrapper'>
          <IconSearch />
          <input
            className='tm-search-input'
            placeholder={
              viewMode === 'members' ? 'Search users...' : 'Search invites...'
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        {canManageMembers && (
          <div className='tm-header-buttons'>
            <button
              className='tm-btn tm-btn-outline'
              onClick={() => setInviteMode(true)}
            >
              <IconUserPlus /> Invite User
            </button>
            <button
              className='tm-btn tm-btn-outline'
              onClick={() => handleTabChange('Assign')}
            >
              <IconPencil /> Edit Licenses
            </button>
            <button
              className='tm-btn tm-btn-outline'
              onClick={() => setNewMode(true)}
            >
              + Create New Team
            </button>
            <button
              className='tm-btn tm-btn-delete-team'
              onClick={() => setDeleteMode(true)}
            >
              <IconTrash /> Delete Team
            </button>
          </div>
        )}
      </div>

      {/* View Toggle */}
      <div className='tm-view-toggle'>
        <button
          className={`tm-btn ${
            viewMode === 'members' ? 'tm-btn-primary' : 'tm-btn-outline'
          }`}
          onClick={() => handleViewChange('members')}
        >
          <IconUsers /> Members{' '}
          <span className='tm-toggle-count'>({members.length})</span>
        </button>
        <button
          className={`tm-btn ${
            viewMode === 'pending' ? 'tm-btn-primary' : 'tm-btn-outline'
          }`}
          onClick={() => handleViewChange('pending')}
        >
          <IconClock /> Pending Invites{' '}
          <span className='tm-toggle-count'>({pending.length})</span>
        </button>
      </div>

      {/* License Overview */}
      {viewMode === 'members' && (
        <div className='tm-license-overview'>
          <span className='tm-license-overview-label'>License Overview:</span>
          {(['standard', 'pro', 'business', 'enterprise'] as LicenseType[]).map(
            (license) => {
              const inv = licenseInventory[license];
              const isLow = inv.total > 0 && inv.total - inv.used <= 1;
              return (
                <div key={license} className='tm-license-item'>
                  <LicenseBadge license={license} />
                  <span
                    className={
                      isLow
                        ? 'tm-license-count tm-license-count-low'
                        : 'tm-license-count tm-license-count-normal'
                    }
                  >
                    {inv.used}/{inv.total}
                    {isLow && <IconAlertCircle />}
                  </span>
                </div>
              );
            },
          )}
          {cur_user.role === 'owner' && (
            <button
              className='tm-btn tm-btn-primary tm-license-overview-buy-btn'
              onClick={() => handleTabChange('Subscriptions')}
            >
              <IconShoppingCart /> Buy More Licenses
            </button>
          )}
        </div>
      )}

      {/* Low License Warning */}
      {viewMode === 'members' &&
        (() => {
          const licenseTypes = [
            'standard',
            'pro',
            'business',
            'enterprise',
          ] as LicenseType[];
          const hasPurchasedLicenses = licenseTypes.some(
            (licenseType) => licenseInventory[licenseType].total > 0,
          );
          const hasAvailableLicenses = licenseTypes.some((licenseType) => {
            const inv = licenseInventory[licenseType];
            return inv.total - inv.used > 0;
          });

          return hasPurchasedLicenses && !hasAvailableLicenses;
        })() && (
          <div className='tm-license-warning'>
            <div className='tm-warning-content'>
              <IconAlertCircle className='tm-warning-icon' />
              <div>
                <div className='tm-warning-title'>
                  You&apos;ve used all available licenses
                </div>
                <div className='tm-warning-subtitle'>
                  Purchase additional licenses to give access to more team
                  members
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Members Table */}
      {viewMode === 'members' && (
        <div className='tm-table-wrapper'>
          <table className='tm-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>
                  <div className='tm-th-filter'>
                    <button
                      className='tm-filter-btn'
                      onClick={() => setShowLicenseDropdown((v) => !v)}
                    >
                      License <IconChevronDown />
                    </button>
                    {licenseFilter !== 'all' && (
                      <button
                        className='tm-filter-clear'
                        onClick={() => handleLicenseFilter('all')}
                        title='Clear filter'
                      >
                        X
                      </button>
                    )}
                    {showLicenseDropdown && (
                      <div className='tm-filter-dropdown'>
                        <button
                          className={`tm-filter-option${
                            licenseFilter === 'all' ? ' tm-filter-active' : ''
                          }`}
                          onClick={() => handleLicenseFilter('all')}
                        >
                          All
                        </button>
                        {LICENSE_OPTIONS.map((l) => (
                          <button
                            key={l}
                            className={`tm-filter-option${
                              licenseFilter === l ? ' tm-filter-active' : ''
                            }`}
                            onClick={() => handleLicenseFilter(l)}
                          >
                            <LicenseBadge license={l} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </th>
                <th>
                  <button className='tm-sort-btn' onClick={cycleLoginSort}>
                    Logins
                    <span className='tm-sort-arrows'>
                      <span
                        className={
                          loginSort === 'asc' ? 'tm-sort-active' : 'tm-sort-dim'
                        }
                      >
                        ▲
                      </span>
                      <span
                        className={
                          loginSort === 'desc'
                            ? 'tm-sort-active'
                            : 'tm-sort-dim'
                        }
                      >
                        ▼
                      </span>
                    </span>
                  </button>
                </th>
                <th>Role</th>
                <th style={{ textAlign: 'right', paddingRight: '16px' }}>
                  <button
                    className='tm-btn tm-btn-outline tm-btn-sm'
                    onClick={handleExportCSV}
                    style={{ padding: '4px 12px', fontSize: '13px' }}
                  >
                    Export CSV
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.map((m: any) => (
                <tr key={m.id}>
                  <td className='tm-cell-name'>
                    {m.firstName ? m.firstName : ''}{' '}
                    {m.lastName ? m.lastName : ''}
                  </td>
                  <td className='tm-cell-muted'>{m.email}</td>
                  <td>
                    <LicenseBadge license={m.license} />
                  </td>
                  <td className='tm-cell-muted-sm'>
                    {m.isLoginCountLoading ? (
                      <em>Calculating...</em>
                    ) : (
                      m.loginsDisplay
                    )}
                  </td>
                  <td>
                    <select
                      className='tm-role-select'
                      value={normalizeRole(
                        m.role ??
                          (isTeamAdmin(cur_user?.selected_team, m?.email)
                            ? 'Admin'
                            : 'Contributor'),
                      )}
                      disabled={Boolean(
                        !canManageMembers ||
                          m.scimManaged ||
                          roleUpdateByMemberId[
                            String(resolveMemberId(m) ?? m.id)
                          ] ||
                          normalizeRole(m.role) === 'Owner' ||
                          normalizeRole(m.role) === 'Restricted',
                      )}
                      title={
                        m.scimManaged
                          ? "This member's role is managed by their organization's identity provider and cannot be changed here."
                          : normalizeRole(m.role) === 'Restricted'
                          ? "This member's role comes from their email domain and cannot be changed here."
                          : undefined
                      }
                      onChange={(event) => {
                        updateMemberRole(
                          String(resolveMemberId(m) ?? m.id),
                          normalizeRole(event.target.value),
                        );
                      }}
                    >
                      {normalizeRole(m.role) === 'Owner' && (
                        <option value='Owner'>Owner</option>
                      )}

                      {/* Display only: Restricted is never offered as a change,
                          and a Restricted member's select is disabled above */}
                      {normalizeRole(m.role) === 'Restricted' && (
                        <option value='Restricted'>Restricted</option>
                      )}

                      <option value='Admin'>Admin</option>
                      <option value='Contributor'>Contributor</option>
                    </select>
                  </td>
                  <td>
                    {canManageMembers &&
                      !isTeamAdmin(cur_user?.selected_team, m?.email) && (
                        <button
                          className='tm-action-btn tm-action-btn-danger'
                          title='Remove member'
                          onClick={() => handleRemove(m)}
                        >
                          <IconTrash />
                        </button>
                      )}
                  </td>
                </tr>
              ))}
              {paginatedMembers.length === 0 && (
                <tr>
                  <td colSpan={6} className='tm-cell-empty'>
                    No team members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending Invites Table */}
      {viewMode === 'pending' && (
        <div className='tm-table-wrapper'>
          <table className='tm-table'>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>License</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedPending.map((inv: any, index: number) => (
                <tr key={inv?.id ?? `${inv?.email ?? inv}-${index}`}>
                  <td>
                    <div className='tm-invite-email'>
                      <IconMail />
                      <span>{inv?.email ?? inv}</span>
                    </div>
                  </td>
                  <td>
                    <span className='tm-badge-pending'>
                      <IconClock /> Pending
                    </span>
                  </td>
                  <td>
                    <LicenseBadge license={inv.preassigned_license_type} />
                  </td>
                  {canManageMembers && (
                    <td>
                      <div className='tm-action-group'>
                        <button
                          className='tm-action-btn'
                          title='Resend invite'
                          onClick={() => handleResend(inv?.email ?? inv)}
                        >
                          <IconRefreshCw />
                        </button>
                        <button
                          className='tm-action-btn tm-action-btn-danger'
                          title='Revoke invite'
                          onClick={() => handleRevoke(inv?.email ?? inv)}
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {paginatedPending.length === 0 && (
                <tr>
                  <td colSpan={4} className='tm-cell-empty'>
                    No pending invites found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className='tm-pagination'>
        <span>
          Showing {(currentPage - 1) * itemsPerPage + 1}-
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
        </span>
        <div className='tm-pagination-buttons'>
          <button
            className='tm-btn tm-btn-outline tm-btn-sm'
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`tm-btn tm-btn-outline tm-btn-sm${
                currentPage === page ? ' tm-current-page' : ''
              }`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className='tm-btn tm-btn-outline tm-btn-sm'
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
      <InviteModal
        open={inviteMode}
        onClose={() => setInviteMode(false)}
        setInviteMode={setInviteMode}
        sendInvite={sendInvite}
        licenseInventory={licenseInventory}
        existingPendingInvites={pending}
      />
      <Modal open={inviteFailMode} onClose={() => setInviteFailMode(false)}>
        <InviteFailModal
          setInviteFailMode={setInviteFailMode}
          handleTabChange={handleTabChange}
        />
      </Modal>
      <Modal open={removeMode} onClose={() => setRemoveMode(false)}>
        <RemoveUserModal
          removeUserFromGroup={removeUserFromGroup}
          user={selectedUser.data}
          setRemoveMode={setRemoveMode}
        />
      </Modal>
      <Modal open={newMode} onClose={() => setNewMode(false)}>
        <CreateTeamModal
          setNewMode={setNewMode}
          newTeamName={newTeamName}
          setNewTeamName={setNewTeamName}
          handleCreateNew={handleCreateNew}
          createTeamInviteEmails={createTeamInviteEmails}
          currentCreateTeamInviteEmail={currentCreateTeamInviteEmail}
          setCurrentCreateTeamInviteEmail={setCurrentCreateTeamInviteEmail}
          handleAddCreateTeamInviteEmail={handleAddCreateTeamInviteEmail}
          handleRemoveCreateTeamInviteEmail={handleRemoveCreateTeamInviteEmail}
          handleCreateTeamInviteKeyDown={handleCreateTeamInviteKeyDown}
        />
      </Modal>
      <Modal open={deleteMode} onClose={() => setDeleteMode(false)}>
        <DeleteTeamModal
          team={cur_user.selected_team}
          handleClose={() => setDeleteMode(false)}
          type='Team'
          cur_user={cur_user}
        />
      </Modal>
    </div>
  );
};

export default TeamManagement;
